<script setup lang="ts">
import { computed, ref } from 'vue'
import { RouterView, useRoute, useRouter } from 'vue-router'
import { useQueryClient } from '@tanstack/vue-query'
import { useAuthStore, useLogoutMutation } from '@modules/auth'
import { logger } from '@core/logging/logger'
import { useAccountStatusQuery, useKycStatusQuery, useUserProfileQuery } from '@modules/account'
import {
  NotificationsDrawer,
  useNotificationsQuery,
  useNotificationsStore,
} from '@modules/notifications'
import { AskSliceyDrawer, useAskSliceyStore } from '@modules/askSlicey'
import { routes } from '@app/router/routes'
import Box from '@uiKit/elements/Box.vue'
import AppTopNav from './AppTopNav.vue'
import AppSideNav from './AppSideNav.vue'
import AppMobileNavDrawer from './AppMobileNavDrawer.vue'
import AskAiFab from './AskAiFab.vue'
import AppFooter from './AppFooter.vue'

const authStore = useAuthStore()
// Prime the profile + notifications cache on app shell mount so AppTopNav and
// every container that subscribes to these queries gets cached data immediately.
// useKycStatusQuery primes the lightweight KYC status used by the nav chips —
// the heavier /api/kyc/details query is only fetched by the Profile page.
useUserProfileQuery()
useAccountStatusQuery()
useKycStatusQuery()
const notificationsQuery = useNotificationsQuery()
const notificationsStore = useNotificationsStore()
const askSliceyStore = useAskSliceyStore()
const unreadCount = computed(
  () => (notificationsQuery.data.value ?? []).filter((notification) => !notification.isRead).length,
)
const router = useRouter()
const route = useRoute()
const queryClient = useQueryClient()
const mobileNavOpen = ref(false)

const { mutateAsync: logoutOnServer } = useLogoutMutation()

// Logout always tears down local state — even when the server call fails
// (network down, server unreachable, 401 because the access token already
// expired). Otherwise an investor on a shared laptop could find themselves
// stuck "signed in" in the UI when they hit logout. Cache is cleared so the
// next investor logging in on this device starts with no stale data.
//
// The refresh token is part of the logout payload (auth-service uses it
// to find and revoke the session row). Capture it before clearing the
// store so the request still has it.
const handleLogout = async () => {
  const refreshToken = authStore.refreshToken
  // For UAE Pass sessions the server returns a front-channel single-logout URL;
  // we redirect the browser there (after clearing local state) so the UAE Pass
  // IdP session is ended too. It then redirects back to the portal login.
  let uaePassLogoutUrl: string | null = null
  try {
    if (refreshToken) {
      const result = await logoutOnServer(refreshToken)
      uaePassLogoutUrl = result.uaePassLogoutUrl
    }
  } catch (error) {
    logger.warn('Server-side logout failed; clearing local session anyway', { error })
  } finally {
    authStore.logout()
    queryClient.clear()
    if (uaePassLogoutUrl) {
      window.location.href = uaePassLogoutUrl
    } else {
      router.push(routes.LOGIN)
    }
  }
}
</script>

<template>
  <Box class="app-shell">
    <AppSideNav />

    <Box direction="col" class="app-shell__main">
      <AppTopNav
        :unread-count="unreadCount"
        @toggle-mobile-nav="mobileNavOpen = true"
        @open-notifications="notificationsStore.openDrawer()"
        @logout="handleLogout"
      />

      <Box is="main" class="app-shell__content">
        <!-- Page wrapper grows to fill remaining height so the footer hugs the
             bottom when content is short. When content overflows, the wrapper
             grows past the viewport and normal scrolling kicks in. -->
        <Box
          class="app-shell__page"
          :class="{ 'app-shell__page--full-height': route.meta.fullHeight }"
        >
          <RouterView />
        </Box>
        <AppFooter />
      </Box>
    </Box>

    <AppMobileNavDrawer v-model="mobileNavOpen" @logout="handleLogout" />

    <AskAiFab />

    <NotificationsDrawer
      :open="notificationsStore.drawerOpen"
      @close="notificationsStore.closeDrawer()"
    />

    <AskSliceyDrawer :open="askSliceyStore.drawerOpen" @close="askSliceyStore.closeDrawer()" />
  </Box>
</template>

<style scoped>
.app-shell {
  height: 100dvh;
  overflow: hidden;
  background: var(--p-sliceal-page-background);
  display: flex;
  flex-direction: row;
}

.app-shell__main {
  flex: 1;
  min-width: 0;
  min-height: 0;
  height: 100%;
}

.app-shell__content {
  flex: 1;
  overflow-y: auto;
  scrollbar-width: none;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.app-shell__content::-webkit-scrollbar {
  display: none;
}

.app-shell__page {
  /* flex: 1 0 auto — grow to fill remaining height (pushes footer to the
     bottom on short pages); also let content overflow naturally when it's
     taller than the viewport (auto basis). */
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.app-shell__page--full-height {
  /* Pages that manage their own internal scrolling (e.g. transaction history)
     must be constrained to the viewport so height:100% inside them resolves
     correctly and overflow-y:auto on their inner list actually creates a
     scroll container. flex-shrink:1 + flex-basis:0 prevents the page from
     growing past the available viewport height. */
  flex: 1 1 0%;
  overflow: hidden;
}
</style>
